import React, { useState, useCallback, useEffect, useMemo } from 'react';

import { Column } from 'primereact/column';
import { toast } from 'react-toastify';
import { Container, SituacaoTag } from '../Licenca/styles';

import AuthService from '../../services/AuthService';
import AfvService from '../../services/AfvService';
import { errorHandle } from '../../util/functions';

import BotaoMenuGrid from '../../components/BotaoMenuGrid';
import ButtonSp from '../../components/ButtonSp';
import DropdownSp from '../../components/DropdownSp';
import InputTextSp from '../../components/InputTextSp';
import LabelSp from '../../components/LabelSp';
import CrudHeader from '../../components/CrudHeader';
import DataTableSp from '../../components/DataTableSp';

import DialogoAcao from './dialogoAcao';
import DialogoStatus from './dialogoStatus';
import PainelRecursos from './painelRecursos';
import {
  ACOES,
  CORES_CONTAINER,
  CORES_SITUACAO,
  NOMES_CONTAINER,
  NOMES_SITUACAO,
  OPCOES_SITUACAO,
  SITUACOES_LIXO,
} from './util';

// AFV dos clientes (um container por contrato na VPS do Google). Lista o que existe no
// servidor cruzado com o portal (cliente inativo, sem modulo ou sem cliente = lixo) e os
// clientes com o modulo que ainda nao tem AFV. So a equipe Speedy.
export default function Afv() {
  const [lista, setLista] = useState([]);
  const [recursos, setRecursos] = useState(undefined); // undefined = carregando
  const [situacao, setSituacao] = useState('');
  const [busca, setBusca] = useState('');
  const [contratoNovo, setContratoNovo] = useState('');
  const [verStatus, setVerStatus] = useState(null);
  const [pedido, setPedido] = useState(null); // { acao, numeroContrato, nomeCliente }

  const podeAlterar = AuthService.checkRoles('AFV_ALTERAR');
  const podeExcluir = AuthService.checkRoles('AFV_EXCLUIR');

  const carrega = useCallback(async () => {
    setRecursos(undefined);
    AfvService.recursos()
      .then(setRecursos)
      .catch(() => setRecursos(null));
    try {
      setLista(await AfvService.listar());
    } catch (err) {
      errorHandle(err);
    }
  }, []);

  useEffect(() => {
    carrega();
  }, [carrega]);

  const filtrada = useMemo(() => {
    const texto = busca.trim().toLowerCase();
    return lista.filter(l => {
      if (situacao === 'LIXO' && !SITUACOES_LIXO.includes(l.situacao)) return false;
      if (situacao && situacao !== 'LIXO' && l.situacao !== situacao) return false;
      if (!texto) return true;
      const nome = l.cliente ? `${l.cliente.nome} ${l.cliente.nomeFantasia || ''}` : '';
      return String(l.numeroContrato).includes(texto) || nome.toLowerCase().includes(texto);
    });
  }, [lista, situacao, busca]);

  async function executa(confirmacao) {
    const { acao, numeroContrato } = pedido;
    setPedido(null);
    try {
      await AfvService.acao(numeroContrato, acao, confirmacao);
      toast.success(`${ACOES[acao].titulo}: concluído (contrato ${numeroContrato}).`);
      if (acao === 'limpar') {
        toast.info('No Gerencial do cliente, sincronize o cadastro com a opção "Tudo".', {
          autoClose: false,
        });
      }
    } catch (err) {
      errorHandle(err);
    }
    carrega();
  }

  function nomeDo(rowData) {
    return rowData.cliente ? rowData.cliente.nomeFantasia || rowData.cliente.nome : '';
  }

  function pede(acao, rowData) {
    setPedido({ acao, numeroContrato: rowData.numeroContrato, nomeCliente: nomeDo(rowData) });
  }

  return (
    <Container className="container-page page-enter-tab">
      <div className="p-grid">
        <CrudHeader title="AFV (servidor)" showButtonFilter={false} showButtonConfig={false} />

        <PainelRecursos recursos={recursos} />

        {/* acoes por contrato a esquerda; filtro da lista a direita */}
        <div
          className="p-col-12"
          style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'flex-end', gap: 8 }}
        >
          <div className="p-fluid" style={{ width: 130 }}>
            <LabelSp>Contrato</LabelSp>
            <InputTextSp
              value={contratoNovo}
              maxLength={6}
              placeholder="Ex.: 180002"
              onChange={e => setContratoNovo(e.target.value.replace(/\D/g, ''))}
            />
          </div>
          <ButtonSp
            className="p-button-success"
            icon="pi pi-play"
            label="Iniciar / criar AFV"
            disabled={!podeAlterar || contratoNovo.length !== 6}
            onClick={() =>
              setPedido({
                acao: 'iniciar',
                numeroContrato: Number(contratoNovo),
                nomeCliente: '',
              })
            }
          />
          <ButtonSp
            className="p-button-secondary"
            icon="pi pi-info-circle"
            label="Status"
            disabled={contratoNovo.length !== 6}
            onClick={() => setVerStatus(Number(contratoNovo))}
          />

          <div
            style={{
              marginLeft: 'auto',
              display: 'flex',
              flexWrap: 'wrap',
              alignItems: 'flex-end',
              gap: 8,
            }}
          >
            <div className="p-fluid" style={{ width: 260 }}>
              <LabelSp>Situação</LabelSp>
              <DropdownSp
                options={OPCOES_SITUACAO}
                value={situacao}
                onChange={e => setSituacao(e.target.value)}
              />
            </div>
            <div className="p-fluid" style={{ width: 240 }}>
              <LabelSp>Buscar</LabelSp>
              <InputTextSp
                value={busca}
                placeholder="Contrato ou cliente"
                onChange={e => setBusca(e.target.value)}
              />
            </div>
            <ButtonSp
              className="p-button-secondary"
              icon="pi pi-refresh"
              title="Atualizar lista e recursos"
              onClick={carrega}
            />
          </div>
        </div>

        <div className="p-col-12 p-fluid datatable-doc-demo">
          <DataTableSp
            value={filtrada}
            style={{ marginBottom: '2px' }}
            paginator
            rows={15}
            responsive
          >
            <Column
              field="numeroContrato"
              header="Contrato"
              className="grid-col-curr"
              style={{ width: 100 }}
            />
            <Column field="cliente.nome" header="Cliente" className="grid-col" body={nomeDo} />
            <Column field="porta" header="Porta" className="grid-col" style={{ width: 80 }} />
            <Column
              field="container"
              header="Container"
              className="grid-col"
              style={{ width: 130 }}
              body={rowData =>
                rowData.container ? (
                  <SituacaoTag cor={CORES_CONTAINER[rowData.container] || '#6c757d'}>
                    {NOMES_CONTAINER[rowData.container] || rowData.container}
                  </SituacaoTag>
                ) : (
                  ''
                )
              }
            />
            <Column
              header="CPU / Memória"
              className="grid-col"
              style={{ width: 130 }}
              body={rowData => {
                const c = recursos && recursos.containers[rowData.numeroContrato];
                return c ? `${c.cpu}% / ${c.memoria}` : '';
              }}
            />
            <Column
              field="qtdAfv"
              header="Módulo"
              className="grid-col"
              style={{ width: 80 }}
            />
            <Column
              field="situacao"
              header="Situação"
              className="grid-col"
              style={{ width: 180 }}
              body={rowData => (
                <SituacaoTag cor={CORES_SITUACAO[rowData.situacao] || '#6c757d'}>
                  {NOMES_SITUACAO[rowData.situacao] || rowData.situacao}
                </SituacaoTag>
              )}
            />
            <Column
              className="gid-col-acoes-35"
              bodyStyle={{ textAlign: 'end' }}
              body={renderButtonOp}
            />
          </DataTableSp>
        </div>
      </div>

      {verStatus ? (
        <DialogoStatus numeroContrato={verStatus} onClose={() => setVerStatus(null)} />
      ) : null}

      {pedido ? (
        <DialogoAcao
          acao={pedido.acao}
          numeroContrato={pedido.numeroContrato}
          nomeCliente={pedido.nomeCliente}
          onConfirm={executa}
          onClose={() => setPedido(null)}
        />
      ) : null}
    </Container>
  );

  function renderButtonOp(rowData) {
    const rodando = rowData.container === 'running';
    const existe = rowData.porta !== null;
    return (
      <BotaoMenuGrid
        handles={[
          () => setVerStatus(rowData.numeroContrato),
          () => pede('iniciar', rowData),
          () => pede('parar', rowData),
          () => pede('reiniciar', rowData),
          () => pede('backup', rowData),
          () => pede('limpar', rowData),
          () => pede('remover', rowData),
        ]}
        labels={[
          'Status / log',
          'Iniciar',
          'Parar',
          'Reiniciar',
          'Backup',
          'Limpar dados',
          'Remover',
        ]}
        icons={[
          'pi pi-info-circle',
          'pi pi-play',
          'pi pi-pause',
          'pi pi-refresh',
          'pi pi-save',
          'pi pi-trash',
          'pi pi-times-circle',
        ]}
        disableds={[
          !existe,
          !podeAlterar || rodando || !['OK', 'NAO_CRIADO'].includes(rowData.situacao),
          !podeAlterar || !rodando,
          !podeAlterar || !existe || rowData.container === 'inexistente',
          !podeAlterar || !existe,
          !podeExcluir || !existe,
          !podeExcluir || !existe,
        ]}
      />
    );
  }
}
